import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface TutorialStep {
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  action?: string;
}

export interface PageTutorial {
  page: string;
  title: string;
  steps: TutorialStep[];
}

interface TutorialProgress {
  hasCompletedOnboarding: boolean;
  completedTutorials: string[];
  tutorialEnabled: boolean;
}

interface TutorialContextType {
  currentTutorial: PageTutorial | null;
  currentStep: number;
  isTutorialActive: boolean;
  tutorialEnabled: boolean;
  hasCompletedOnboarding: boolean;
  isProgressLoaded: boolean;
  startTutorial: (tutorial: PageTutorial) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  completeTutorial: () => void;
  setTutorialEnabled: (enabled: boolean) => void;
  completeOnboarding: () => void;
  isStepCompleted: (pageId: string) => boolean;
  resetTutorial: () => void;
  resetCount: number;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentTutorial, setCurrentTutorial] = useState<PageTutorial | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [isProgressLoaded, setIsProgressLoaded] = useState(false);
  const [progress, setProgress] = useState<TutorialProgress>({
    hasCompletedOnboarding: false,
    completedTutorials: [],
    tutorialEnabled: true,
  });
  const [resetCount, setResetCount] = useState(0);

  useEffect(() => {
    if (user) {
      setIsProgressLoaded(false);
      loadTutorialProgress();
    } else {
      setIsProgressLoaded(false);
    }
  }, [user]);

  const loadTutorialProgress = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_tutorial_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProgress({
          hasCompletedOnboarding: data.has_completed_onboarding,
          completedTutorials: data.completed_tutorials || [],
          tutorialEnabled: data.tutorial_enabled,
        });
      } else {
        const { error: insertError } = await supabase
          .from('user_tutorial_progress')
          .insert({
            user_id: user.id,
            has_completed_onboarding: false,
            completed_tutorials: [],
            tutorial_enabled: true,
          });

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('Failed to load tutorial progress:', error);
    } finally {
      setIsProgressLoaded(true);
    }
  };

  const updateProgress = async (updates: Partial<TutorialProgress>) => {
    if (!user) return;

    const newProgress = { ...progress, ...updates };
    setProgress(newProgress);

    try {
      const { error } = await supabase
        .from('user_tutorial_progress')
        .upsert({
          user_id: user.id,
          has_completed_onboarding: newProgress.hasCompletedOnboarding,
          completed_tutorials: newProgress.completedTutorials,
          tutorial_enabled: newProgress.tutorialEnabled,
        }, { onConflict: 'user_id' });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to update tutorial progress:', error);
    }
  };

  const startTutorial = (tutorial: PageTutorial) => {
    setCurrentTutorial(tutorial);
    setCurrentStep(0);
    setIsTutorialActive(true);
  };

  const nextStep = () => {
    if (currentTutorial && currentStep < currentTutorial.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTutorial = () => {
    setIsTutorialActive(false);
    setCurrentTutorial(null);
    setCurrentStep(0);
  };

  const completeTutorial = () => {
    if (currentTutorial && !progress.completedTutorials.includes(currentTutorial.page)) {
      const updatedTutorials = [...progress.completedTutorials, currentTutorial.page];
      updateProgress({ completedTutorials: updatedTutorials });
    }
    setIsTutorialActive(false);
    setCurrentTutorial(null);
    setCurrentStep(0);
  };

  const setTutorialEnabled = (enabled: boolean) => {
    updateProgress({ tutorialEnabled: enabled });
  };

  const completeOnboarding = () => {
    updateProgress({ hasCompletedOnboarding: true });
  };

  const isStepCompleted = (pageId: string) => {
    return progress.completedTutorials.includes(pageId);
  };

  const resetTutorial = () => {
    updateProgress({
      hasCompletedOnboarding: true,
      completedTutorials: [],
      tutorialEnabled: true,
    });
    setResetCount(c => c + 1);
    try { localStorage.removeItem('tg_welcome_dismissed'); } catch {}
  };

  return (
    <TutorialContext.Provider
      value={{
        currentTutorial,
        currentStep,
        isTutorialActive,
        tutorialEnabled: progress.tutorialEnabled,
        hasCompletedOnboarding: progress.hasCompletedOnboarding,
        isProgressLoaded,
        startTutorial,
        nextStep,
        previousStep,
        skipTutorial,
        completeTutorial,
        setTutorialEnabled,
        completeOnboarding,
        isStepCompleted,
        resetTutorial,
        resetCount,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}
